<template>
    <vx-modal :visible="props.visible"
        @update:visible="emit('update:visible', false)"
        :padding="false"
        size="md"
        :stacked="configuring"
        >
        <template v-slot:header>
            <div class="pt-6 px-6 flex items-center gap-2">
                <span>
                    AI Message Assistant
                </span>
                <experimental-pill class="-mt-1"></experimental-pill>
            </div>
        </template>

        <div class="py-4 px-5 flex flex-col gap-8 lg:py-7 lg:px-10">
            <ai-configuration-status v-if="!generating" @saved="refetch()" v-model:configuring="configuring" :unavailable="!!(error && configured)"></ai-configuration-status>

            <div v-if="generating" data-test="generating-messages" class="flex flex-col items-center justify-center py-6 space-y-8">
                <div class="overflow-hidden">
                    <vx-icon-loading class="w-9 h-9 animate-loading-spin"></vx-icon-loading>
                </div>
                <p class="text-center text-base text-primary lg:px-24">
                    Your messages are being generated...
                </p>
            </div>

            <div v-else-if="allSuggestions.length">
                <h4 class="text-base mb-3 font-bold text-slate-700">
                    Generated Messages
                </h4>
                <message-generation-list v-model="selected"
                    :messages="allSuggestions"></message-generation-list>
            </div>
        </div>

        <template v-slot:footer>
            <div class="flex gap-4">
                <template v-if="generating || error || !configured">
                    <vx-button @click.prevent="emit('update:visible', false)"
                        type="button"
                        color="muted"
                        size="lg"
                        class="grow">
                        Cancel <font-awesome-icon widthAuto :icon="faCircleXmark"></font-awesome-icon>
                    </vx-button>
                </template>
                <template v-else-if="allSuggestions.length">
                    <vx-button class="grow-0"
                        color="muted-dark"
                        size="lg"
                        type="button"
                        @click="generate()">
                        Generate New
                        <font-awesome-icon widthAuto :icon="faWandMagicSparkles"></font-awesome-icon>
                    </vx-button>
                    <vx-button class="grow"
                        type="button"
                        size="lg"
                        @click="confirm()"
                        data-test="confirm-message">
                        Confirm
                        <font-awesome-icon widthAuto :icon="faArrowRight"></font-awesome-icon>
                    </vx-button>
                </template>
            </div>
        </template>
    </vx-modal>
</template>

<script setup>
import {
    faArrowRight,
    faWandMagicSparkles,
    faCircleXmark,
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { VxModal, VxButton, VxIconLoading } from '@voxie/frontend-components';
import { ref, watch, computed } from 'vue';
import { actionsClient, aiClient, openAISettingsClient, trackingClient } from '~/services';
import { useToasted } from '~/composables/useToasted';
import MessageGenerationList from './MessageGenerationList.vue';
import ExperimentalPill from "~/components/general/ExperimentalPill.vue";
import AiConfigurationStatus from "~/components/settings/ai-configuration/AiConfigurationStatus.vue"

const props = defineProps({
    contactId: {
        type: Number,
        required: true,
    },
    groupId: {
        type: String,
        default: undefined,
    },
    visible: Boolean,
});

const emit = defineEmits(['selected', 'update:visible']);

const toasted = useToasted();
const configuring = ref(false);

const allSuggestions = ref([]);
const selected = ref("");

const error = ref(false);
const generating = ref(false);
const generationRequestId = ref(0);

const config = ref(undefined);

const configured = computed(() => config.value?.settings?.tone && config.value?.settings?.role);

const getAiConfig = async () => {
    if (configured.value) {
        return
    }

    try {
        config.value = (await openAISettingsClient.getConfig(Spark.state.currentTeam.id)).data;
    } catch(e) {
        console.error(e)
        config.value = undefined;
    }
}

const generate = async () => {
    error.value = false;

    if (!configured.value) {
        return;
    }

    const requestId = ++generationRequestId.value;
    generating.value = true;
    allSuggestions.value = [];

    const teamId = Spark.state.currentTeam.id;
    const params = { contact_id: props.contactId };
    const nextSuggestions = [];

    actionsClient.send('message_hub_ai_message_generated');
    trackingClient.track('messagehub/messageBuilder/aiGenerated');

    const promises = Array.from({ length: 3 }, () =>
        aiClient.messageHubSuggestions({ teamId, groupId: props.groupId }, params)
            .then((response) => {
                if (requestId !== generationRequestId.value) {
                    return;
                }

                const message = response.data.data?.message;
                if (message?.length) {
                    nextSuggestions.push(message);
                }
            })
            .catch((e) => {
                console.error(e);
            })
    );

    await Promise.allSettled(promises);

    if (requestId !== generationRequestId.value) {
        return;
    }

    allSuggestions.value = nextSuggestions;

    if (!allSuggestions.value.length) {
        error.value = true;
        toasted.error('Failed to generate messages. Please try again.');
    }

    generating.value = false;
};

const confirm = () => {
    if (!selected.value) {
        selected.value = allSuggestions.value[0]
    }

    emit("selected", selected.value);
    emit('update:visible', false);
    reset();
};

const reset = () => {
    generationRequestId.value++;
    generating.value = false;
    allSuggestions.value = [];
    selected.value = "";
    error.value = false;
}

const refetch = async () => {
    await getAiConfig();
    generate();
}

watch([() => props.visible, () => props.groupId], async ([visible, _groupId], previousValues) => {
    const previousVisible = previousValues?.[0];
    const previousGroupId = previousValues?.[1];

    if (!visible) {
        reset();
        return;
    }

    await getAiConfig();

    if (!allSuggestions.value.length || previousGroupId !== props.groupId || !previousVisible) {
        generate();
    }
}, {
    immediate: true
})

</script>
